// crewbit-admin/src/pages/Money/Activity.tsx
//
// Arc C3 P4 — full Money activity feed (§Money.RecentActivitySeeAllDestination).
// Destination of the RecentActivityCard "See all ›" tail. Paginated via
// useMoneyActivityFeed (P1 get_money_activity), with a money-flow type filter
// (All / Money in / Money out) and a from/to date range. Read-only.
//
// Refresh policy is OWNED by useMoneyActivityFeed (window MONEY_MUTATION_EVENT
// listener). This page adds NO visibility/window listener of its own (§10.10).

import { useEffect, useState } from 'react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { EmptyState } from '../../components/common/EmptyState';
import { Skeleton } from '../../components/common/Skeleton';
import SettingsGearLink from '../../components/money/settings/SettingsGearLink';
import {
  useMoneyActivityFeed,
  type MoneyActivityType,
} from '../../hooks/useMoneyActivityFeed';
import type { FinancialSummaryActivityItem } from '../../hooks/useFinancialSummary';
import { activityTypeToPill, type ActivityPillColor } from '../../lib/activityPills';
import { formatMoney } from '../../lib/money';
import { admin, palette, radii, shadow, spacing, transition, typography } from '../../styles/tokens';

const pageStyle: React.CSSProperties = {
  padding: spacing.xl,
  background: admin.bg,
  minHeight: '100vh',
};

const TYPE_OPTIONS: { key: MoneyActivityType | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'in', label: 'Money in' },
  { key: 'out', label: 'Money out' },
];

function toneColor(color: ActivityPillColor): string {
  switch (color) {
    case 'success': return palette.green;
    case 'warning': return palette.amber;
    case 'info': return palette.accent;
    case 'neutral': default: return palette.g500;
  }
}

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function MoneyActivityPage() {
  const [type, setType] = useState<MoneyActivityType | 'all'>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const feed = useMoneyActivityFeed({
    type: type === 'all' ? null : type,
    from: from || null,
    to: to || null,
  });

  useEffect(() => {
    document.title = 'CrewBit · Activity';
  }, []);

  const dateInputStyle: React.CSSProperties = {
    padding: '6px 10px',
    borderRadius: radii.sm,
    border: `1px solid ${admin.border}`,
    background: palette.white,
    fontSize: typography.size.caption,
    fontFamily: 'inherit',
    color: admin.body,
  };

  return (
    <div style={pageStyle}>
      <PageHeader
        title="Activity"
        back={{ to: '/money', label: 'Money' }}
        description="All money activity"
        secondaryActions={<SettingsGearLink />}
      />

      {/* Filters: type segmented + date range */}
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md, flexWrap: 'wrap', marginBottom: spacing.lg }}>
        <div role="tablist" aria-label="Activity type" style={{ display: 'flex', background: palette.g100, borderRadius: 8, padding: 2 }}>
          {TYPE_OPTIONS.map((opt) => {
            const active = opt.key === type;
            return (
              <button
                key={opt.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setType(opt.key)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: typography.size.caption,
                  fontWeight: active ? typography.weight.semibold : typography.weight.medium,
                  background: active ? palette.g800 : 'transparent',
                  color: active ? palette.white : palette.g500,
                  transition: `all ${transition.fast}`,
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: typography.size.caption, color: admin.caption }}>
          From
          <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} style={dateInputStyle} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: typography.size.caption, color: admin.caption }}>
          To
          <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} style={dateInputStyle} />
        </label>
        {(from || to) && (
          <button
            type="button"
            onClick={() => { setFrom(''); setTo(''); }}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: typography.size.caption, color: palette.accent }}
          >
            Clear dates
          </button>
        )}
      </div>

      {/* Body */}
      {feed.isLoading ? (
        <div style={cardStyle}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} style={{ padding: '12px 16px', borderBottom: i === 5 ? 'none' : `1px solid ${palette.g100}` }}>
              <Skeleton width="60%" height={12} />
              <div style={{ height: 6 }} />
              <Skeleton width="30%" height={10} />
            </div>
          ))}
        </div>
      ) : feed.error ? (
        <EmptyState title="Couldn't load activity" subtitle="Check your connection and try again." />
      ) : feed.rows.length === 0 ? (
        <EmptyState
          title={type !== 'all' || from || to ? 'No matching activity' : 'Nothing here yet'}
          subtitle={type !== 'all' || from || to ? 'Try clearing the filters.' : 'Money activity will appear here as it happens.'}
        />
      ) : (
        <>
          <div style={cardStyle}>
            {feed.rows.map((item, idx) => (
              <ActivityRow key={item.id} item={item} isLast={idx === feed.rows.length - 1} />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: spacing.lg }}>
            {feed.hasMore ? (
              <button
                type="button"
                onClick={feed.loadMore}
                disabled={feed.isLoadingMore}
                style={{
                  padding: '8px 18px',
                  borderRadius: radii.sm,
                  border: `1px solid ${admin.border}`,
                  background: palette.white,
                  cursor: feed.isLoadingMore ? 'default' : 'pointer',
                  fontFamily: 'inherit',
                  fontSize: typography.size.caption,
                  fontWeight: typography.weight.semibold,
                  color: admin.body,
                }}
              >
                {feed.isLoadingMore ? 'Loading…' : 'Load more'}
              </button>
            ) : (
              <span style={{ fontSize: typography.size.caption, color: admin.caption }}>
                {feed.total} {feed.total === 1 ? 'item' : 'items'} · end of list
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: palette.white,
  borderRadius: admin.cardRadius,
  border: `1px solid ${admin.border}`,
  overflow: 'hidden',
  boxShadow: shadow.card,
};

function ActivityRow({ item, isLast }: { item: FinancialSummaryActivityItem; isLast: boolean }) {
  const pill = activityTypeToPill(item.activity_type);
  const showAmount = item.amount !== null && item.direction !== null;
  const amountColor = item.direction === 'in' ? palette.green : palette.red;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.sm,
        padding: '12px 16px',
        borderBottom: isLast ? 'none' : `1px solid ${palette.g100}`,
      }}
    >
      {pill.label !== '' && (
        <span
          style={{
            flexShrink: 0,
            padding: '1px 8px',
            borderRadius: radii.full,
            background: palette.g100,
            color: toneColor(pill.color),
            fontSize: typography.size.micro,
            fontWeight: typography.weight.semibold,
            letterSpacing: '0.02em',
          }}
        >
          {pill.label}
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0, fontSize: typography.size.caption, color: admin.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {item.description ?? ''}
      </span>
      {showAmount && (
        <span style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0, color: amountColor, fontSize: typography.size.caption, fontWeight: typography.weight.semibold, fontVariantNumeric: 'tabular-nums' }}>
          {item.direction === 'in' ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
          {item.direction === 'in' ? '+' : '−'}{formatMoney(Math.abs(item.amount as number))}
        </span>
      )}
      <span style={{ flexShrink: 0, minWidth: 56, textAlign: 'right', fontSize: typography.size.micro, color: admin.caption }}>
        {relTime(item.created_at)}
      </span>
    </div>
  );
}
